import { memo, useCallback, useContext, useEffect, useState } from 'react';
import { ConfirmDialog } from '../../../components/Modal/ConfirmDialog';
import { RendererContext } from '../../../contexts/RendererContext';
import { T } from '../../../translation/src';

interface LayoutBootstrapPromptEvent {
  /** Floors with rooms + an outline and no wall sketch — can generate. */
  floorCount?: number;
  /** Floors with rooms but NO outline — cannot generate until one is drawn. */
  missingOutlineCount?: number;
}

interface LayoutPersonaWithBootstrap {
  name: string;
  addEventListener(event: string, handler: (e: LayoutBootstrapPromptEvent) => void): void;
  removeEventListener(event: string, handler: (e: LayoutBootstrapPromptEvent) => void): void;
  runBootstrap(): void;
  cancelBootstrap(): void;
}

/**
 * Mounts the dialog shown when entering the Layout persona for floors that
 * have rooms but no wall sketch yet:
 *  - floors WITH an outline → confirm dialog offering to generate a wall
 *    sketch on each (the original bootstrap flow);
 *  - floors WITHOUT an outline → these CANNOT generate (the sketch is built
 *    from the rooms scoped by the floor's outline), so the dialog explains
 *    that instead of silently skipping them. When no floor can generate at
 *    all, a warning-only dialog replaces the creation prompt entirely.
 * Subscribes to `layout_bootstrap_prompt` / `layout_bootstrap_dismiss`
 * events on the Layout persona.
 */
const LayoutWallSketchBootstrap_: React.FC = () => {
  const { viewer } = useContext(RendererContext);
  const [open, setOpen] = useState(false);
  const [floorCount, setFloorCount] = useState(0);
  const [missingOutlineCount, setMissingOutlineCount] = useState(0);

  useEffect(() => {
    if (!viewer?.personaManager) return;

    const layoutPersona = viewer.personaManager.getAllPersonas().find((p: { name: string }) => p.name === 'layout') as LayoutPersonaWithBootstrap | undefined;
    if (!layoutPersona) return;

    const onPrompt = (e: LayoutBootstrapPromptEvent) => {
      setFloorCount(e.floorCount ?? 0);
      setMissingOutlineCount(e.missingOutlineCount ?? 0);
      setOpen(true);
    };
    const onDismiss = () => {
      setOpen(false);
    };

    layoutPersona.addEventListener('layout_bootstrap_prompt', onPrompt);
    layoutPersona.addEventListener('layout_bootstrap_dismiss', onDismiss);

    return () => {
      layoutPersona.removeEventListener('layout_bootstrap_prompt', onPrompt);
      layoutPersona.removeEventListener('layout_bootstrap_dismiss', onDismiss);
    };
  }, [viewer]);

  const handleConfirm = useCallback(() => {
    if (!viewer?.personaManager) return;
    const layoutPersona = viewer.personaManager.getAllPersonas().find((p: { name: string }) => p.name === 'layout') as LayoutPersonaWithBootstrap | undefined;
    layoutPersona?.runBootstrap();
  }, [viewer]);

  const handleClose = useCallback(() => {
    if (!viewer?.personaManager) return;
    const layoutPersona = viewer.personaManager.getAllPersonas().find((p: { name: string }) => p.name === 'layout') as LayoutPersonaWithBootstrap | undefined;
    layoutPersona?.cancelBootstrap();
    setOpen(false);
  }, [viewer]);

  // Warning-only: every floor that needs a wall sketch is missing its
  // outline — there is nothing to generate, so no creation prompt.
  if (floorCount === 0) {
    return (
      <ConfirmDialog
        open={open}
        onClose={handleClose}
        onConfirm={handleClose}
        variant="warning"
        hideConfirm
        title={<T _str="cannot generate wall sketches" swc />}
        message={<T _str="found {count} floor(s) with rooms but no outer wall. wall sketches are generated from a floor's outer wall. draw an outer wall on those floors first, then use generate walls in the floor's menu." swc args={{ count: missingOutlineCount }} />}
        cancelLabel={<T _str="ok" swc />}
      />
    );
  }

  return (
    <ConfirmDialog
      open={open}
      onClose={handleClose}
      onConfirm={handleConfirm}
      variant="info"
      title={<T _str="create wall sketches from rooms?" swc />}
      message={
        missingOutlineCount > 0 ? (
          <>
            <T _str="found {count} floor(s) with rooms but no wall sketch. generate a wall sketch on each?" swc args={{ count: floorCount }} />
            <span className="block mt-2 text-warning-yellow-700 dark:text-warning-yellow-500">
              <T _str="{count} other floor(s) have rooms but no outer wall and will be skipped. draw an outer wall on those floors first." swc args={{ count: missingOutlineCount }} />
            </span>
          </>
        ) : (
          <T _str="found {count} floor(s) with rooms but no wall sketch. generate a wall sketch on each?" swc args={{ count: floorCount }} />
        )
      }
      confirmLabel={<T _str="generate" swc />}
      cancelLabel={<T _str="skip" swc />}
    />
  );
};

export const LayoutWallSketchBootstrap = memo(LayoutWallSketchBootstrap_);
